<template>
  <div class="bg-white">
    <div class="">
      <h2 class="sr-only">Checkout</h2>

      <div class="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16">
        <div>
          <div class="font-poppins">
            <h2 class="text-2xl font-poppins-bold text-black">Contact information</h2>

            <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 gap-x-10 font-poppins">
              <div>
                <label for="first-name" class="block text-sm font-bold text-black">First name</label>
                <div class="mt-1 flex">
                  <input type="text" id="first-name" name="first-name" autocomplete="given-name" v-model="formData.firstName" @blur="handleFirstNameBlur" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" :disabled="verified"/>
                </div>
                <div v-for="error of vContact$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="firstName-error">
                  <p v-if="error.$property === 'firstName'">
                    {{ error.$message }}
                  </p>
                </div>
              </div>

              <div>
                <label for="last-name" class="block text-sm font-bold text-black">Last name</label>
                <div class="mt-1 flex">
                  <input type="text" id="last-name" name="last-name" autocomplete="family-name" v-model="formData.lastName" @blur="handleLastNameBlur" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" :disabled="verified" />
                </div>
                <div v-for="error of vContact$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="lastName-error">
                  <p v-if="error.$property === 'lastName'">
                    {{ error.$message }}
                  </p>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <label for="email-address" class="block text-sm font-bold text-black">Email address</label>
              <div class="mt-1 flex">
                <input type="email" id="email-address" name="email-address" autocomplete="email" v-model="formData.email" @blur="handleEmailBlur" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none"  :disabled="verified"/>
              </div>
              <div v-for="error of vContact$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="email-error">
                <p v-if="error.$property === 'email'">
                  {{ error.$message }}
                </p>
              </div>
            </div>

            <div class="sm:col-span-2 mt-4">
              <label for="phone" class="block text-sm font-bold text-black">Phone</label>
              <div class="mt-1 flex relative">
                <input type="text" name="phone" id="phone" autocomplete="tel" v-model="formData.phoneNumber" @blur="handlePhoneBlur" :class="[
                  'w-full border rounded px-3 py-2 pr-10 transition',
                  verified
                    ? 'bg-green-50 border-green-500 text-green-700 cursor-not-allowed rounded-full'
                    : 'block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none'
                ]" :disabled="verified"/>
              <CheckCircleIcon
                v-if="verified"
                class="w-5 h-5 text-green-600 absolute right-3 top-1/2 -translate-y-1/2"
              />
              </div>
              <div v-for="error of vContact$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="phoneNumber-error">
                <p v-if="error.$property === 'phoneNumber'">
                  {{ error.$message }}
                </p>
              </div>
            </div>

            <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
              <div class="">
                <label for="activationDateOption" class="block text-sm font-bold text-black">Service activation date</label>
                <div class="mt-1">
                  <select id="activationDateOption" v-model="formData.formSelectionOptions.pickActivationDate" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" :disabled="verified" @change="handleActivationDate">
                    <option v-bind:value="false">ASAP</option>
                    <option v-bind:value="true">Pick a Date</option>
                  </select>
                </div>
              </div>

              <div v-show="formData.formSelectionOptions.pickActivationDate" class="">
                <label for="activationDate" class="block text-sm font-bold text-black">Confirm service activation date</label>
                <div class="mt-1 flex">
                  <input type="date" :min="tomorrow" name="activationDate" id="activationDate" v-model="formData.activationDate" @blur="v$.activationDate.$touch" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" :disabled="verified" />
                </div>
                <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="activationDate-error">
                  <p v-if="error.$property === 'activationDate'">
                    {{ error.$message }}
                  </p>
                </div>
              </div>
            </div>

            <div v-if="orderService.calculateMinimumCost(products, isPromoValid, validPromotion) >= 1000" class="mt-4 font-poppins">
              <p class="text-sm text-black font-poppins tracking-tight pt-0 py-0 my-0">Based on the total value of your order, and in accordance with our obligations under the 2019 TCP Code, Nexee is required to run an external credit check before we can confirm your order. This may delay your service activation as it is dependent on the credit check being completed first.</p>
              <label for="birthdate" class="block text-sm font-bold text-black font-poppins pt-4">Birthdate</label>
              <div class="mt-1 flex">
                <input type="date" :max="min18" name="birthdate" id="birthdate" v-model="formData.birthdate" @blur="v$.birthdate.$touch" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" :disabled="verified" />
              </div>
              <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="bday-error">
                <p v-if="error.$property === 'birthdate'">
                  {{ error.$message }}
                </p>
              </div>
              <label for="License Id" class="block text-sm font-bold text-black font-poppins pt-4">License Id</label>
              <div class="mt-1 flex">
                <input type="text" id="licenseId" name="licenseId" autocomplete="licenseId" v-model="formData.licenseId" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" :disabled="verified" />
              </div>
              <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="licenseid-error">
                <p v-if="error.$property === 'licenseId'">
                  {{ error.$message }}
                </p>
              </div>
            </div>

            <div class="mt-6 flex space-x-2">
              <div class="flex items-center h-5">
                <input @click="handleClickBusinessAccount" id="same-as-shipping" name="same-as-shipping" type="checkbox" v-model="formData.formSelectionOptions.businessAccount" class="h-4 w-4 border-black border-solid border-1 rounded text-black focus:ring-brand-primary"  :disabled="verified"/>
              </div>
              <label for="same-as-shipping" class="text-sm font-bold text-gray-900">Is this a business account?</label>
            </div>

            <div v-if="formData.formSelectionOptions.businessAccount" class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 gap-x-10 font-poppins">
              <div>
                <label for="company-name" class="block text-sm font-bold text-black">Company name</label>
                <div class="mt-1">
                  <input type="text" id="company-name" name="company-name" autocomplete="organization" v-model="formData.companyName" @blur="v$.companyName.$touch" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none"  :disabled="verified"/>
                </div>
                <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="companyName-error">
                  <p v-if="error.$property === 'companyName'">
                    {{ error.$message }}
                  </p>
                </div>
              </div>

              <div>
                <label for="abn-number" class="block text-sm font-bold text-black">ABN Number</label>
                <div class="mt-1 flex">
                  <input type="text" id="abn-number" name="abn-number" v-model="formData.abnNumber" @blur="v$.abnNumber.$touch" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none"  :disabled="verified"/>
                </div>
                <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="abnNumber-error">
                  <p v-if="error.$property === 'abnNumber'">
                    {{ error.$message }}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="mt-10 lg:mt-0">

          <!-- Address Confirmation -->
          <h2 class="text-2xl font-poppins-bold text-black">Confirm Details</h2>

          <div class="font-poppins">
            <div class="mt-4 bg-white ">
              <div class="py-6 px-4 space-y-6 sm:px-6">
                <div class="flex transition duration-200 justify-between items-center  rounded-lg py-6 px-6 "
                  :class="[formData.formSelectionOptions.confirmAddressToggle ? 'bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark text-white' : 'border-solid border-black border text-black']">
                  <div class="">
                    <div class="min-w-0 flex-1">
                        <p class="text-2xl font-light py-0 my-0">
                          My address is:
                        </p>
                        <p class="text-lg py-0 my-0">
                          {{ service.address }}
                        </p>
                      </div>
                    </div>
                  <div class="text-base font-medium text-gray-900">
                    <Switch
                      v-model="formData.formSelectionOptions.confirmAddressToggle"
                      :disabled="verified"
                      :class="[
                        (formData.formSelectionOptions.confirmAddressToggle ? 'bg-white' : 'bg-gray-300'),
                        (verified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'),
                        ('relative inline-flex flex-shrink-0 h-6 w-11 border-1 border-transparent rounded-full transition-colors ease-in-out duration-200 my-0 py-0'),
                        (confirmAddressError ? 'ring-1 ring-offset-1 ring-brand-primary' : 'focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-brand-primary')
                        ]"
                      >
                        <span class="sr-only">Use setting</span>
                        <div :class="[formData.formSelectionOptions.confirmAddressToggle ? 'translate-x-4 bg-brand-primary' : 'translate-x-0 bg-white', 'pointer-events-none relative inline-block h-5 w-5 rounded-full  shadow transform ring-0 transition ease-in-out duration-200 -ml-1']">
                          <div :class="[formData.formSelectionOptions.confirmAddressToggle ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                          <div :class="[formData.formSelectionOptions.confirmAddressToggle ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                        </div>
                    </Switch>
                  </div>
                </div>
                <div class="flex transition duration-200 justify-between items-center rounded-lg py-6 px-2 md:px-6 "
                  :class="[formData.formSelectionOptions.termsAgreementToggle ? 'bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark text-white' : 'border-solid border-black border text-black']">
                  <div class="text-md font-light pr-6">
                    I have read, understood and accept the <a :href="`https://uniti-bpo-website-assets.assets.unitiinternet.com/${brandService.getName()}/customer-service-agreement.pdf`" target="_blank" class="hover:underline transition duration-250 font-bold" :class="[formData.formSelectionOptions.termsAgreementToggle ? 'text-white' : 'text-black']">Terms and Conditions.</a>
                  </div>
                  <div class="text-base font-medium text-gray-900">
                    <Switch
                      v-model="formData.formSelectionOptions.termsAgreementToggle"
                      :disabled="verified"
                      :class="[
                        (formData.formSelectionOptions.termsAgreementToggle ? 'bg-white' : 'bg-gray-300'),
                        (verified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'),
                        ('relative inline-flex flex-shrink-0 h-6 w-11 border-1 border-transparent rounded-full transition-colors ease-in-out duration-200 my-0 py-0'),
                        (termsAgreementError ? 'ring-1 ring-offset-1 ring-brand-primary' : 'focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-brand-primary')
                        ]"
                      >
                        <span class="sr-only">Use setting</span>
                        <div :class="[formData.formSelectionOptions.termsAgreementToggle ? 'translate-x-4 bg-brand-primary' : 'translate-x-0 bg-white', 'pointer-events-none relative inline-block h-5 w-5 rounded-full  shadow transform ring-0 transition ease-in-out duration-200 -ml-1']">
                          <div :class="[formData.formSelectionOptions.termsAgreementToggle ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                          <div :class="[formData.formSelectionOptions.termsAgreementToggle ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                        </div>
                    </Switch>
                  </div>
                </div>
                <div v-if="voiceProduct"  class="flex transition duration-200 justify-between items-center rounded-lg py-6 px-2 md:px-6 "
                  :class="[formData.formSelectionOptions.csgWaiverToggle ? 'bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark text-white' : 'border-solid border-black border text-black']">
                  <div class="text-md font-light" @click="toggleExpandCsgWaiver()">
                    I have read, understood and accept the <span class="underline transition duration-250 font-bold" :class="[formData.formSelectionOptions.csgWaiverToggle ? 'text-white' : 'text-black']">CSG waiver.</span>

                    <p class="text-red-400" v-if="formData.formSelectionOptions.csgWaiverToggle && salesPerson">Ensure you have read the <a class="text-red-600" :href="$router.resolve({ name: 'csg-waiver-script' }).href" target="_blank">CSG Waiver Script</a></p>
                  </div>
                  <div class="text-base font-medium text-gray-900">
                    <Switch
                      v-model="formData.formSelectionOptions.csgWaiverToggle"
                      :disabled="verified"
                      :class="[
                        (formData.formSelectionOptions.csgWaiverToggle ?  'bg-white' : 'bg-gray-300'),
                        (verified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'),
                        ('relative inline-flex flex-shrink-0 h-6 w-11 border-1 border-transparent rounded-full transition-colors ease-in-out duration-200 my-0 py-0'),
                        (csgWaiverError ? 'ring-1 ring-offset-1 ring-brand-primary' : 'focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-brand-primary')
                        ]"
                      >
                        <span class="sr-only">Use setting</span>
                        <div :class="[formData.formSelectionOptions.csgWaiverToggle ? 'translate-x-4 bg-brand-primary' : 'translate-x-0 bg-white', 'pointer-events-none relative inline-block h-5 w-5 rounded-full  shadow transform ring-0 transition ease-in-out duration-200 -ml-1']">
                          <div :class="[formData.formSelectionOptions.csgWaiverToggle ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                          <div :class="[formData.formSelectionOptions.csgWaiverToggle ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                        </div>
                    </Switch>
                  </div>
                </div>
                <div v-if="voiceProduct && formData.formSelectionOptions.toggleExpandCsgWaiver" class="flex items-center justify-between border-t border-gray-200 pt-6" >
                  <div class="text-sm font-light h-64 overflow-hidden">
                    <CsgTerms :firstName="contactInformation.firstName"
                              :lastName="contactInformation.lastName"
                              :brand="brandService.getTitle()"
                              :phone="brandService.getSupportPhoneNumber()"
                              :address="brandService.getAddress()"
                    />
                  </div>
                </div>
                <div class="flex transition duration-200 justify-between items-center rounded-lg py-6 px-2 md:px-6 "
                  :class="[formData.formSelectionOptions.ddAgreementToggle ? 'bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark text-white' : 'border-solid border-black border text-black']">
                  <div class="text-md font-light pr-6">
                    I have read, understood and accept the <a :href="`https://uniti-bpo-website-assets.assets.unitiinternet.com/${brandService.getName()}/direct-debit-service-request-agreement.pdf`" target="_blank" class="underline transition duration-250 font-bold" :class="[formData.formSelectionOptions.ddAgreementToggle ? 'text-white' : 'text-black']">Direct Debit Service Request Agreement.</a>
                  </div>
                  <div class="text-base font-medium text-gray-900">
                    <Switch
                      v-model="formData.formSelectionOptions.ddAgreementToggle"
                      :disabled="verified"
                      :class="[
                        (formData.formSelectionOptions.ddAgreementToggle ?  'bg-white' : 'bg-gray-300'),
                        (verified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'),
                        ('relative inline-flex flex-shrink-0 h-6 w-11 border-1 border-transparent rounded-full transition-colors ease-in-out duration-200 my-0 py-0'),
                        (ddAgreementError ? 'ring-1 ring-offset-1 ring-brand-primary' : 'focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-brand-primary')
                        ]"
                      >
                      <span class="sr-only">Use setting</span>
                      <div :class="[formData.formSelectionOptions.ddAgreementToggle ? 'translate-x-4 bg-brand-primary' : 'translate-x-0 bg-white', 'pointer-events-none relative inline-block h-5 w-5 rounded-full  shadow transform ring-0 transition ease-in-out duration-200 -ml-1']">
                          <div :class="[formData.formSelectionOptions.ddAgreementToggle ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                          <div :class="[formData.formSelectionOptions.ddAgreementToggle ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                        </div>
                    </Switch>
                  </div>
                </div>
                <div class="flex transition duration-200 justify-between items-center rounded-lg py-6 px-2 md:px-6 "
                  :class="[formData.formSelectionOptions.privacyToggle ? 'bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark text-white' : 'border-solid border-black border text-black']">
                  <div class="text-md font-light pr-6">
                    I consent to {{ brandService.getTitle() }} collecting, using, storing and disclosing my personal information in accordance with the {{ brandService.getTitle() }} Privacy Policy. I have read, understood and agree to comply with the {{ brandService.getTitle() }} <a :href="`https://uniti-bpo-website-assets.assets.unitiinternet.com/${brandService.getName()}/privacy-policy.pdf`" target="_blank" class="underline transition duration-250 font-bold text-black">Privacy Policy</a>.
                  </div>
                  <div class="text-base font-medium text-gray-900">
                    <Switch
                      v-model="formData.formSelectionOptions.privacyToggle"
                      :disabled="verified"
                      :class="[
                        (formData.formSelectionOptions.privacyToggle ?  'bg-white' : 'bg-gray-300'),
                        (verified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'),
                        ('relative inline-flex flex-shrink-0 h-6 w-11 border-1 border-transparent rounded-full transition-colors ease-in-out duration-200 my-0 py-0'),
                        (privacyError ? 'ring-1 ring-offset-1 ring-brand-primary' : 'focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-brand-primary')
                        ]"
                      >
                      <span class="sr-only">Use setting</span>
                      <div :class="[formData.formSelectionOptions.privacyToggle ? 'translate-x-4 bg-brand-primary' : 'translate-x-0 bg-white', 'pointer-events-none relative inline-block h-5 w-5 rounded-full  shadow transform ring-0 transition ease-in-out duration-200 -ml-1']">
                          <div :class="[formData.formSelectionOptions.privacyToggle ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                          <div :class="[formData.formSelectionOptions.privacyToggle ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                        </div>
                    </Switch>
                  </div>
                </div>
                <div v-if="isNbnService" class="flex transition duration-200 justify-between items-center rounded-lg py-6 px-2 md:px-6 "
                  :class="[formData.formSelectionOptions.additionalFeesToggle ? 'bg-gradient-to-tr from-brand-primary-light to-brand-primary-dark text-white' : 'border-solid border-black border text-black']">
                  <div class="text-md font-light pr-6">
                    I understand that additional fees may apply to my service and will be charged to me where applicable, as outlined in the <a :href="cisUrl" target="_blank" class="underline transition duration-250 font-bold" :class="[formData.formSelectionOptions.additionalFeesToggle ? 'text-white' : 'text-black']">Critical Information Summary</a>.
                  </div>
                  <div class="text-base font-medium text-gray-900">
                    <Switch
                      v-model="formData.formSelectionOptions.additionalFeesToggle"
                      :disabled="verified"
                      :class="[
                        (formData.formSelectionOptions.additionalFeesToggle ?  'bg-white' : 'bg-gray-300'),
                        (verified ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'),
                        ('relative inline-flex flex-shrink-0 h-6 w-11 border-1 border-transparent rounded-full transition-colors ease-in-out duration-200 my-0 py-0'),
                        (additionalFeesError ? 'ring-1 ring-offset-1 ring-brand-primary' : 'focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-brand-primary')
                        ]"
                      >
                      <span class="sr-only">Use setting</span>
                      <div :class="[formData.formSelectionOptions.additionalFeesToggle ? 'translate-x-4 bg-brand-primary' : 'translate-x-0 bg-white', 'pointer-events-none relative inline-block h-5 w-5 rounded-full  shadow transform ring-0 transition ease-in-out duration-200 -ml-1']">
                          <div :class="[formData.formSelectionOptions.additionalFeesToggle ? 'opacity-0 ease-out duration-100' : 'opacity-100 ease-in duration-200', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                          <div :class="[formData.formSelectionOptions.additionalFeesToggle ? 'opacity-100 ease-in duration-200' : 'opacity-0 ease-out duration-100', 'absolute inset-0 h-full w-full flex items-center justify-center transition']" aria-hidden="true">
                          </div>
                        </div>
                    </Switch>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
        <div>
          <!-- Order Summary -->
        </div>
        <div v-if="!verified" class="items-end flex flex-col justify-end text-right pr-6">
          <AsyncButton @onClick="handleContactInformation" class="">Confirm Contact Information</AsyncButton>
          <p v-if="confirmAddressError || termsAgreementError || ddAgreementError || csgWaiverError || privacyError || additionalFeesError" class="mt-4 text-sm text-red-600">
            Please confirm your details and tick the required fields
          </p>
        </div>
        <div v-if="verified" class="items-end flex justify-end w-full text-right">
          <AsyncButton @onClick="editDetails" class="">Edit Details</AsyncButton>
        </div>
      </div>
      <transition name="fade-scale">
        <div
          v-if="otpStep === 2" ref="otpStepRef"
          class="fixed inset-0 z-50 flex items-center justify-center"
        >
           <!-- Backdrop -->
          <div
            class="absolute inset-0 bg-black/40"
            @click="closeOtp"
          ></div>

            <div
              ref="modalRef"
              class="relative z-10 w-full max-w-md bg-white rounded-2xl p-6 shadow-xl"
            >
            <div class="text-left col-start-1 col-span-2 flex flex-col">
              <p class="font-benzin text-black text-2xl tracking-normal font-normal py-0 my-0">VERIFY YOUR<span class="text-brand-primary"> PHONE NUMBER</span></p>
              <div v-if="!verified">
                <OtpVerification
                  :phone-number="formData.phoneNumber?.toString()"
                  purpose="phone_verification"
                  :active="otpStep === 2"
                  @verified="handleVerified"
                />
              </div>

              <div v-else class="text-left py-4">
                <h2 class="text-xl font-semibold text-green-600 font-poppins">
                  Phone verified successfully
                </h2>

              </div>
              <button
                class="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-medium hover:bg-red-700"
                @click="closeOtp"
              >
                Cancel
              </button>

            </div>
          </div>
        </div>
      </transition>
      <div
        v-if="verified" ref="paymentStep"
        class="grid grid-cols-1 md:grid-cols-3 z-50 relative pt-6"
      >
        <div class="my-6 md:my-12">
          <hr>
        </div>
        <div class="hidden md:block text-right col-span-1 col-start-3 ">
          <StepGuide :current-step="6"/>
        </div>
      </div>
      <div
        v-if="verified"
        class="lg:grid lg:grid-cols-2 lg:gap-x-12 xl:gap-x-16"
      >
        <div>
          <!-- Payment -->
          <div class=" border-t border-gray-200 pt-6">
            <label for="paymentMethodOptions" class="text-2xl font-poppins-bold text-black">Choose your payment method</label>
            <select id="paymentMethodOptions" v-model="formData.formSelectionOptions.paymentMethod" class="block mt-2 font-poppins w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" @change="handleActivationDate">
              <option v-bind:value="'cc'">Credit Card</option>
              <option v-bind:value="'dd'">Bank Account</option>
            </select>
            <h2 class="text-2xl font-poppins-bold text-black">Enter your Details</h2>

            <div v-if="!directDebitFormFields" class="mt-6 font-poppins">
              <label class="block text-sm font-bold text-black">Card Details</label>
              <div v-if="monoovaLoading" class="mt-2 text-sm">
                Loading secure payment form...
              </div>
              <button
                v-else-if="!monoovaSessionActive"
                type="button"
                class="mt-2 px-6 py-2 rounded-full bg-black text-white text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!monoovaReady"
                @click="ensureMonoovaCheckout"
              >
                Enter Card Details
              </button>
              <div ref="monoovaContainer" class="w-full mt-2" />
              <div v-if="monoovaError" class="mt-2">
                <p class="text-sm text-red-600">{{ monoovaError }}</p>
                <button type="button" class="underline text-sm mt-1" @click="retryMonoovaCheckout">
                  Try again
                </button>
              </div>
              <p v-if="cardEntryError" class="mt-2 text-sm text-red-600">
                Please enter your card details
              </p>
            </div>
            <div v-if="directDebitFormFields" class="mt-6 grid grid-cols-4 gap-y-6 gap-x-4 font-poppins">
                <div class="col-span-4">
                  <label for="account-name" class="block text-sm font-bold text-black">Account Name</label>
                  <div class="mt-1 flex">
                    <input type="text" name="account-name" id="account-number" v-model="formData.ddAccountName" @blur="v$.ddAccountName.$touch" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" />
                  </div>
                  <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="ddAccountName-error">
                    <p v-if="error.$property === 'ddAccountName'">
                      {{ error.$message }}
                    </p>
                  </div>
                </div>
                <div class="col-span-3">
                  <label for="account-number" class="block text-sm font-bold text-black">Account Number</label>
                  <div class="mt-1 flex">
                    <input type="text" name="account-number" id="account-number" v-model="formData.ddAccountNumber" @blur="v$.ddAccountNumber.$touch" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" />
                  </div>
                  <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="ddAccountNumber-error">
                    <p v-if="error.$property === 'ddAccountNumber'">
                      {{ error.$message }}
                    </p>
                  </div>
                </div>

                <div>
                  <label for="bsb" class="block text-sm font-bold text-black">BSB</label>
                  <div class="mt-1 flex">
                    <input type="text" name="bsb" id="bsb" v-model="formData.ddAccountBsb" @blur="v$.ddAccountBsb.$touch" class="block w-full rounded-full shadow-sm sm:text-sm bg-gray-200 border-none" />
                  </div>
                  <div v-for="error of v$.$errors" :key="error.$uid" class="mt-2 text-sm text-red-600" id="ddAccountBsb-error">
                    <p v-if="error.$property === 'ddAccountBsb'">
                      {{ error.$message }}
                    </p>
                  </div>
                </div>
              </div>
          </div>
        </div>

        <div class="mt-10 lg:mt-0">

          <h2 class="text-2xl font-poppins-bold text-black">Order Summary</h2>
          <!-- Order summary -->

          <div class="mt-4 bg-white font-poppins">
            <OrderSummary :products="products" :isPromoValid="isPromoValid" :promotionDiscounts="promotionDiscounts" :validPromotion="validPromotion" :suggestedPromotion="suggestedPromotion" :promoCode="promoCode" @onEnterPromoCode="(code) => emit('onEnterPromoCode', code)"></OrderSummary>

            <div class="py-6 flex flex-col md:flex-row items-end justify-end">
              <AsyncButton @onClick="handleConfirmOrder" class="pr-6 w-full">Confirm Order</AsyncButton>

              <div class="w-full">
                <p v-if="confirmAddressError || termsAgreementError || ddAgreementError || csgWaiverError || privacyError || additionalFeesError" class="mt-4 text-sm text-red-600">
                  Please confirm your details and tick the required fields
                </p>
                <p v-if="v$.$error" class="mt-4 text-sm text-red-600">
                  Please fill out all required form fields
                </p>
                <p v-for="error in errors" :key="error" class="mt-4 text-sm text-red-600">
                  {{ error }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang='ts'>
import { computed, reactive, defineEmits, defineProps, ref, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, email, required, requiredIf, minLength, maxLength, integer } from '@vuelidate/validators';
import { nameValidations } from '@/validations/nameValidations';
import { OrderServiceInterface } from '@/services/orderServiceInterface';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';
import { Product } from '@/adapters/catalogAdapterInterface';
import { UtilityServiceInterface } from '@/services/utilityServiceInterface';
import AsyncButton from '../library/AsyncButton.vue';
import { BrandServiceInterface } from '@/services/brand/brandServiceInterface';
import { Carrier } from '@/services/integration/enums';
import { MonoovaTokenProviderInterface } from '@/services/integration/clients/monoova/monoovaTokenProviderInterface';
import { mountCardCheckout, extractCardScheme, CardCheckoutHandle, PrimerCheckoutPayment } from '@/services/integration/clients/monoova/monoovaCheckout';
import format from 'date-fns/format';
import addDays from 'date-fns/fp/addDays';
import store from '@/store';
import { CheckCircleIcon } from '@heroicons/vue/solid';
import { RecaptchaV2, useRecaptcha } from 'vue3-recaptcha-v2';
import OtpVerification from '@/components/ordering/OtpVerification.vue';

// Adding this prevents Vue warnings
import { Switch } from '@headlessui/vue';
import { Action, Promotion } from '@uniti-internet/promotions-js-client/dist/utilities/types';
import CsgTerms from '@/components/content/CsgTerms.vue';
import StepGuide from './StepGuide.vue';
import { LoadedCartCustomer } from '@/utils/cartLoader';
import OrderSummary from './OrderSummary.vue';

export type ContactInformation = {
  firstName: string | null,
  lastName: string | null,
  phoneNumber: string | null,
  email: string,
  activationDate: string
};

type Service = {
  id: string,
  address: string,
  carrier: string,
  locationId: string,
  requiresNewDevelopmentCost: boolean,
  development: string,
  suburb: string,
  state: string,
  rtcDays: number,
}

type ConfirmOrderData = {
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: number | null,
  companyName: string,
  abnNumber: number | null,
  ccNumber: number | null,
  ccName: string,
  ccExpiry: string,
  ccCvc: string,
  ccMonoovaToken?: string,
  ccMonoovaScheme?: string,
  ddAccountName: string,
  ddAccountNumber: string,
  ddAccountBsb: string,
  token?: string,
  activationDate: string,
  formSelectionOptions: {
    businessAccount: boolean,
    paymentMethod: string,
    confirmAddressToggle: boolean,
    termsAgreementToggle: boolean,
    privacyToggle: boolean,
    additionalFeesToggle: boolean,
    csgWaiverToggle: boolean,
    toggleExpandCsgWaiver: boolean,
    ddAgreementToggle: boolean,
    pickActivationDate: boolean
  },
  captchaValid: boolean,
  birthdate?: string | null,
  licenseId?: string | null
}

const orderService = injectStrict<OrderServiceInterface>('OrderServiceInterface');
const utilityService = injectStrict<UtilityServiceInterface>('UtilityServiceInterface');
const brandService = injectStrict<BrandServiceInterface>('BrandServiceInterface');
const monoovaTokenProvider = injectStrict<MonoovaTokenProviderInterface>('MonoovaTokenProviderInterface');

const props = defineProps<{
  errors: string[],
  products: Product[],
  service: Service,
  isPromoValid: boolean,
  promotionDiscounts: Array<Action>,
  validPromotion?: Promotion,
  suggestedPromotion?: Promotion,
  promoCode?: string,
  initialCustomer?: LoadedCartCustomer,
  otpStep: number
}>();

const account = computed(() => {
  return store.state.account;
});

const salesPerson = computed(() => {
  return store.state.salesPerson;
});

const isPromoValid = computed(() => {
  return props.isPromoValid;
});

const voiceProduct = computed(() => {
  return props.products.filter(product => product.type === 'voice').length > 0;
});

const today = computed((): string => {
  return format(new Date(), 'yyyy-MM-dd');
});

const tomorrow = computed((): string => {
  return format(addDays(1, new Date()), 'yyyy-MM-dd');
});

const min18 = computed((): string => {
  const td = new Date();
  return format(td.setFullYear(td.getFullYear() - 18), 'yyyy-MM-dd');
});

const verified = ref(false);

const formData : ConfirmOrderData = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: null,
  companyName: '',
  abnNumber: null,
  ccNumber: null,
  ccName: '',
  ccExpiry: '',
  ccCvc: '',
  ccMonoovaToken: '',
  ccMonoovaScheme: '',
  ddAccountName: '',
  ddAccountNumber: '',
  ddAccountBsb: '',
  token: '',
  crmId: null,
  activationDate: today.value,
  formSelectionOptions: {
    businessAccount: false,
    paymentMethod: 'cc',
    confirmAddressToggle: false,
    termsAgreementToggle: false,
    privacyToggle: false,
    additionalFeesToggle: false,
    csgWaiverToggle: false,
    toggleExpandCsgWaiver: false,
    ddAgreementToggle: false,
    pickActivationDate: false
  },
  captchaValid: false,
  birthdate: '',
  licenseId: ''
});

const { handleReset } = useRecaptcha();

const handleWidgetId = (widgetId: number) => {
  handleReset(widgetId);
};
const handleErrorCalback = () => {
  formData.captchaValid = false;
};
const handleExpiredCallback = () => {
  formData.captchaValid = false;
};
const handleLoadCallback = (response: unknown) => {
  if (response) {
    formData.captchaValid = true;
  }
};

const contactInformation : ContactInformation = reactive({
  firstName: null,
  lastName: null,
  phoneNumber: null,
  email: '',
  activationDate: ''
});

// Pre-fill the details from a cart being edited (?cartId=).
if (props.initialCustomer) {
  const customer = props.initialCustomer;
  formData.firstName = customer.firstName;
  formData.lastName = customer.lastName;
  formData.email = customer.email;
  // Kept as a string so a leading 0 isn't lost, same as typing into the input.
  formData.phoneNumber = (customer.phoneNumber || null) as unknown as number | null;
  formData.birthdate = customer.birthdate;
  formData.licenseId = customer.licenseId;

  if (customer.companyName || customer.abnNumber) {
    formData.formSelectionOptions.businessAccount = true;
    formData.companyName = customer.companyName;
    formData.abnNumber = (customer.abnNumber || null) as unknown as number | null;
  }

  // A past activation date falls back to the default of today.
  if (customer.activationDate && customer.activationDate > today.value) {
    formData.formSelectionOptions.pickActivationDate = true;
    formData.activationDate = customer.activationDate;
  }

  contactInformation.firstName = formData.firstName || null;
  contactInformation.lastName = formData.lastName || null;
  contactInformation.phoneNumber = customer.phoneNumber || null;
  contactInformation.email = formData.email;
  contactInformation.activationDate = formData.activationDate;
}

const pickActivationDate = ref(() => formData.formSelectionOptions.pickActivationDate);
const businessAccount = ref(() => formData.formSelectionOptions.businessAccount);
const paymentMethodRequiredDd = ref(() => formData.formSelectionOptions.paymentMethod === 'dd' && (!salesPerson.value));
const bdayRequired = ref(() => orderService.calculateMinimumCost(props.products, props.isPromoValid, props.validPromotion) >= 1000);

const rules = {
  firstName: {
    required: helpers.withMessage('Please enter a firstname', required),
    nameValidation: helpers.withMessage('Please enter letters only', nameValidations.nameValidation),
    maxLengthValue: helpers.withMessage('Please enter less than 20 chars', maxLength(20))
  },
  lastName: {
    required: helpers.withMessage('Please enter a lastname', required),
    nameValidation: helpers.withMessage('Please enter letters only', nameValidations.nameValidation),
    maxLengthValue: helpers.withMessage('Please enter less than 20 chars', maxLength(20))
  },
  email: {
    required: helpers.withMessage('Please enter an email address', required),
    email: helpers.withMessage('Please enter a valid email address', email)
  },
  phoneNumber: {
    required: helpers.withMessage('This field cannot be empty', required),
    integer: helpers.withMessage('Please enter numbers only', integer),
    minLength: helpers.withMessage('Please enter a phone number with 10 digits', minLength(10)),
    maxLengthValue: helpers.withMessage('Please enter a phone number with 10 digits', maxLength(10))
  },
  companyName: {
    requiredIf: helpers.withMessage('Please enter a company name', requiredIf(businessAccount.value)),
    minLength: helpers.withMessage('Please enter a phone number with 10 digits', minLength(1)),
    maxLengthValue: helpers.withMessage('Please enter less than 50 chars', maxLength(50)),
    nameValidation: helpers.withMessage('Please enter letters only', nameValidations.nameValidation)
  },
  abnNumber: {
    requiredIf: helpers.withMessage('Please enter an ABN number', requiredIf(businessAccount.value)),
    integer: helpers.withMessage('Please enter numbers only', integer),
    minLength: helpers.withMessage('Please enter a 11 digit ABN number', minLength(11)),
    maxLength: helpers.withMessage('Please enter a 11 digit ABN number', maxLength(11))
  },
  ddAccountName: {
    requiredIf: helpers.withMessage('Please enter an account name', requiredIf(paymentMethodRequiredDd.value)),
    minLength: helpers.withMessage('Please enter a valid account name', minLength(2))
  },
  ddAccountNumber: {
    requiredIf: helpers.withMessage('Please enter an account number', requiredIf(paymentMethodRequiredDd.value)),
    integer: helpers.withMessage('Please enter numbers only', integer),
    minLength: helpers.withMessage('Please enter a valid account number', minLength(6)),
    maxLength: helpers.withMessage('Please enter a valid account number', maxLength(10))
  },
  ddAccountBsb: {
    requiredIf: helpers.withMessage('Please enter a BSB number', requiredIf(paymentMethodRequiredDd.value)),
    integer: helpers.withMessage('Please enter numbers only', integer),
    minLength: helpers.withMessage('Please enter a 6 digit BSB number', minLength(6)),
    maxLength: helpers.withMessage('Please enter a 6 digit BSB number', maxLength(6))
  },
  activationDate: {
    requiredIf: helpers.withMessage('Please select a date the service should be activated', requiredIf(pickActivationDate.value))
  },
  birthdate: {
    requiredIf: helpers.withMessage('Please enter your birthdate', requiredIf(bdayRequired.value))
  }
};
const v$ = useVuelidate(rules, formData);

const directDebitFormFields = computed(() => {
  return formData.formSelectionOptions.paymentMethod !== 'cc';
});

// eslint-disable-next-line func-call-spacing
const emit = defineEmits<{
  (e: 'onEnterPromoCode', promoCodeInput?: string): void,
  (e: 'onConfirmOrder', formData: ConfirmOrderData, done: () => void): void,
  (e: 'onConfirmContact', contactInformation: ContactInformation, done: () => void): void,
  (e: 'onContactInformation', contactInformation: ContactInformation): void,
  (e: 'onOtpVerified'): void,
  (e: 'closeOtp'): void;
}>();

// True once "Confirm Contact Information" has been clicked at least once - the agreement
// errors below only show once an attempt has actually been made, and stay in sync with the
// toggles live from then on (no separate per-toggle "clear the error" handler needed).
const contactAgreementsAttempted = ref(false);
const confirmAddressError = computed(() => contactAgreementsAttempted.value && !formData.formSelectionOptions.confirmAddressToggle);
const termsAgreementError = computed(() => contactAgreementsAttempted.value && !formData.formSelectionOptions.termsAgreementToggle);
const ddAgreementError = computed(() => contactAgreementsAttempted.value && !formData.formSelectionOptions.ddAgreementToggle);
const privacyError = computed(() => contactAgreementsAttempted.value && !formData.formSelectionOptions.privacyToggle);
const isNbnService = computed(() => props.service.carrier === Carrier.NB);
const additionalFeesError = computed(() => contactAgreementsAttempted.value && isNbnService.value && !formData.formSelectionOptions.additionalFeesToggle);
// Only bind https links on the brand assets host to :href - anything else (javascript:, other
// origins) falls back to the default CIS so the link can't be used for XSS.
const cisUrl = computed(() => {
  const assetsBaseUrl = brandService.getAssetsBaseUrl();
  const fallback = `${assetsBaseUrl}/${brandService.getName()}/critical-information-summary-internet.pdf`;
  const link = brandService.getPlanSelectorProductInformation(props.service.carrier).standardLinks
    .find(standardLink => standardLink.name === 'Critical Information Summary')?.link;
  if (!link) return fallback;
  try {
    const url = new URL(link);
    return url.protocol === 'https:' && url.origin === new URL(assetsBaseUrl).origin ? url.href : fallback;
  } catch {
    return fallback;
  }
});
const csgWaiverError = computed(() => contactAgreementsAttempted.value && voiceProduct.value && !formData.formSelectionOptions.csgWaiverToggle);
const captchaError = ref(false);

const cardEntryError = ref(false);

const monoovaContainer = ref<HTMLElement | null>(null);
const monoovaLoading = ref(false);
const monoovaSessionActive = ref(false);
const monoovaError = ref('');
const cardCheckoutComplete = ref(false);
const cardCheckoutHandle = ref<CardCheckoutHandle | null>(null);
const cardSubmittable = ref(false);
const pendingConfirmOrderDone = ref<(() => void) | null>(null);

const monoovaReady = computed(() => {
  return Boolean(formData.firstName && formData.lastName && formData.email);
});

const ensureMonoovaCheckout = async () => {
  if (monoovaSessionActive.value || monoovaLoading.value || !monoovaReady.value) {
    return;
  }

  const container = monoovaContainer.value;
  if (!container) {
    return;
  }

  container.innerHTML = '';
  monoovaLoading.value = true;
  monoovaError.value = '';
  cardEntryError.value = false;
  cardCheckoutComplete.value = false;
  cardCheckoutHandle.value = null;
  cardSubmittable.value = false;
  formData.ccMonoovaToken = '';
  formData.ccMonoovaScheme = '';

  try {
    // The btbToken is available as soon as the client token is generated -
    // it does not mean the customer has completed entering their card.
    // cardCheckoutComplete (set in onComplete below) tracks that instead.
    const response = await monoovaTokenProvider.generateClientToken(brandService.getName(), {
      emailAddress: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName
    });

    formData.ccMonoovaToken = response.btbToken;

    // Card authorisation is triggered by the shared "Confirm Order" button (see
    // handleConfirmOrder) rather than a submit button inside the widget itself.
    cardCheckoutHandle.value = await mountCardCheckout(response.clientToken, container, {
      onSubmittableChange: (submittable) => {
        cardSubmittable.value = submittable;
      },
      onComplete: (payment: PrimerCheckoutPayment) => {
        formData.ccMonoovaScheme = extractCardScheme(payment);
        cardCheckoutComplete.value = true;
        resolvePendingConfirmOrder(true);
      },
      onFail: () => {
        monoovaSessionActive.value = false;
        cardCheckoutComplete.value = false;
        formData.ccMonoovaToken = '';
        monoovaError.value = 'We are unable to process your credit card. Please double-check all card details are correct. If the issue persists, please contact your card issuer or try an alternative payment method.';
        resolvePendingConfirmOrder(false);
      }
    });

    monoovaSessionActive.value = true;
  } catch (e) {
    console.error(e);
    monoovaError.value = 'Unable to load the secure payment form. Please try again later.';
  } finally {
    monoovaLoading.value = false;
  }
};

const retryMonoovaCheckout = () => {
  monoovaSessionActive.value = false;
  cardCheckoutComplete.value = false;
  monoovaError.value = '';
  ensureMonoovaCheckout();
};

watch(() => formData.formSelectionOptions.paymentMethod, (paymentMethod) => {
  if (paymentMethod !== 'cc') {
    monoovaSessionActive.value = false;
    cardCheckoutComplete.value = false;
    cardCheckoutHandle.value = null;
    cardSubmittable.value = false;
    monoovaError.value = '';
    cardEntryError.value = false;
    formData.ccMonoovaToken = '';
    formData.ccMonoovaScheme = '';
    if (monoovaContainer.value) {
      monoovaContainer.value.innerHTML = '';
    }
  }
});

const contactRules = {
  firstName: rules.firstName,
  lastName: rules.lastName,
  email: rules.email,
  phoneNumber: rules.phoneNumber,
  activationDate: rules.activationDate
};

const vContact$ = useVuelidate(contactRules, contactInformation);

const handleContactInformation = async (done: () => void) => {
  const contactFormValidated = await vContact$.value.$validate();
  contactAgreementsAttempted.value = true;

  // birthdate lives on formData (validated by v$, not vContact$) but is shown in this same
  // Contact Information section, so it needs to be checked here too - otherwise a skipped
  // credit-check field wasn't caught until Confirm Order, by which point the card had
  // already been authorised. licenseId on the same section is optional, so it's untouched.
  // Only actually validate it when the credit-check block (and the field itself) is visible -
  // below the $1000 threshold it's hidden and must not block submission.
  if (bdayRequired.value()) {
    v$.value.birthdate.$touch();
  }
  const creditCheckFieldsValid = !bdayRequired.value() || !v$.value.birthdate.$error;

  const agreementsValid = !confirmAddressError.value && !termsAgreementError.value && !ddAgreementError.value &&
    !privacyError.value && !additionalFeesError.value && !csgWaiverError.value;

  if (!contactFormValidated || !agreementsValid || !creditCheckFieldsValid) {
    done();
    return;
  }
  emit('onConfirmContact', contactInformation, done);
};

// Terms/address/privacy/dd/csg agreements are validated up-front in handleContactInformation
// above, so this is purely the trigger for card or bank account submission from here on.
const handleConfirmOrder = async (done: () => void) => {
  const formValidated = await v$.value.$validate();

  // True once the customer still needs to authorise their card - i.e. paying by card and
  // the widget hasn't already completed a successful authorisation for this session.
  const cardNotYetAuthorised = formData.formSelectionOptions.paymentMethod === 'cc' && !cardCheckoutComplete.value;
  cardEntryError.value = cardNotYetAuthorised && !(cardCheckoutHandle.value && cardSubmittable.value);

  if (!formValidated || cardEntryError.value) {
    done();
    return;
  }

  if (cardNotYetAuthorised) {
    // Card authorisation happens inside the embedded Monoova/Primer widget - trigger it here
    // and wait for onComplete/onFail (see ensureMonoovaCheckout) to resolve this, via
    // resolvePendingConfirmOrder, before the order is actually submitted.
    pendingConfirmOrderDone.value = done;
    cardCheckoutHandle.value?.submit();
    return;
  }

  emit('onConfirmOrder', formData, done);
};

const resolvePendingConfirmOrder = (cardAuthorisationSucceeded: boolean) => {
  const done = pendingConfirmOrderDone.value;
  if (!done) {
    return;
  }

  pendingConfirmOrderDone.value = null;

  if (cardAuthorisationSucceeded) {
    // Re-run validation/submission now that cardCheckoutComplete is true - it will
    // proceed straight to emitting the order rather than re-triggering card auth.
    handleConfirmOrder(done);
  } else {
    done();
  }
};

const handleClickBusinessAccount = () => {
  formData.companyName = '';
  formData.abnNumber = null;
};

const handleActivationDate = () => {
  if (formData.formSelectionOptions.pickActivationDate) {
    formData.activationDate = tomorrow.value;
    contactInformation.activationDate = tomorrow.value;
  } else {
    formData.activationDate = today.value;
    contactInformation.activationDate = today.value;
  }
};

const handleFirstNameBlur = () => {
  v$.value.firstName.$touch();

  if (!v$.value.firstName.$error) {
    contactInformation.firstName = formData.firstName;
  } else {
    contactInformation.firstName = null;
  }
};

const handleLastNameBlur = () => {
  v$.value.lastName.$touch();

  if (!v$.value.lastName.$error) {
    contactInformation.lastName = formData.lastName;
  } else {
    contactInformation.lastName = null;
  }
};

const handlePhoneBlur = () => {
  v$.value.phoneNumber.$touch();

  if (!v$.value.phoneNumber.$error) {
    contactInformation.phoneNumber = formData.phoneNumber;
  } else {
    contactInformation.phoneNumber = null;
  }
};

const handleEmailBlur = () => {
  v$.value.email.$touch();

  if (!v$.value.email.$error) {
    contactInformation.email = formData.email;
  } else {
    contactInformation.email = null;
  }
};

const handleVerified = (data: unknown) => {
  console.log('OTP verified:', data);

  emit('onOtpVerified');

  verified.value = true;
};

// AsyncButton shows a loading state until done() is called.
const editDetails = (done: () => void) => {
  verified.value = false;
  // Back to the start so confirming again goes through OTP/payment afresh.
  closeOtp();
  done();
};

const toggleExpandCsgWaiver = () => {
  formData.formSelectionOptions.toggleExpandCsgWaiver = !formData.formSelectionOptions.toggleExpandCsgWaiver;
};

const paymentStep = ref<HTMLElement | null>(null);

watch(verified, async (ver) => {
  if (ver) { // LAST STEP
    await nextTick();

    paymentStep.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
});

watch(
  contactInformation,
  (newContactInformation) => {
    if (newContactInformation.email) {
      emit('onContactInformation', newContactInformation);
    }
  }
);

const otpStepRef = ref<HTMLElement | null>(null);

const showOtp = ref(false);

const modalRef = ref<HTMLElement | null>(null);

/* ---------------------------
   OPEN / CLOSE
---------------------------- */
const openOtp = () => {
  showOtp.value = true;
};

const closeOtp = () => {
  emit('closeOtp');
};

watch(
  () => props.otpStep,
  async (newStep) => {
    if (newStep === 3) {
      // Payment step, also reached directly when the phone was already verified.
      verified.value = true;
    }

    if (newStep === 2) {
      await nextTick();

      document.body.style.overflow = 'hidden';
      modalRef.value?.focus?.();
    } else {
      document.body.style.overflow = '';
    }
  }
);

/* ---------------------------
   ESC TO CLOSE
---------------------------- */
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    closeOtp();
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
/* Tooltip container */
.tooltip {
  position: relative;
  display: inline-block;
  top: 7px;
  left: 7px;
}

/* Tooltip text */
.tooltip .tooltiptext {
  visibility: hidden;
  width: 300px;
  background-color: black;
  color: #fff;
  text-align: center;
  padding: 5px 0;
  border-radius: 6px;
  position: absolute;
  z-index: 1;
  top: 30px;
  left: -5px;
}

.tooltip .tooltiptext .tooltipmsg {
  margin: 15px 15px 15px 15px;
}

.tooltip .tooltiptext::after {
  content: " ";
  position: absolute;
  bottom: 100%;  /* At the top of the tooltip */
  left: 50%;
  margin-left: -138px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent black transparent;
}

.tooltip .tooltiptext {
  opacity: 0;
  transition: opacity 1s;
}

.tooltip:hover .tooltiptext {
  opacity: 1;
}

/* Show the tooltip text when you mouse over the tooltip container */
.tooltip:hover .tooltiptext {
  visibility: visible;
}

/* FADE + SCALE */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.25s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-scale-enter-to,
.fade-scale-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
